import { FC } from "react";
import { Controller } from "react-hook-form";
import { MedCheckboxListProps } from "./type";
import ReactInputMask from "react-input-mask";
import { format, parse } from "date-fns";
import DatePicker from "react-datepicker";
import Select from "react-select";

const BASE = "prescription_information_attributes.cp_diuretics.diuretics_tried";

export const failureReasons = [
  // "Patient not achieving A1C Goal",
  // "Adverse Event",
  // "Lack of Time in Range",
  // "Contraindication",
  // "Intolerance",
  // "Other",
    "Patient not achieving A1C Goal",
    "Blood Glucose Variability (Lack of Time in Range)",
    "Intolerance to Therapy or Hypersensitivity",
    "Experienced Adverse Event including increased Hypoglycemia",
    "Contraindication",
    "Other",
];

// 🔥 normalize function (IMPORTANT)
const normalize = (name: string) =>
  name.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");

const MedCheckboxList: FC<MedCheckboxListProps> = ({
  meds,
  control,
  watch,
  resetField,
}): JSX.Element => {

  return (
    <>
      {meds.map((label) => {
        const key = normalize(label);

        const checked = watch(`${BASE}.${key}` as any);
        const selectedReason = watch(`${BASE}.${key}_props.${key}_failure_reason` as any);

        return (
          <div key={key} className="mb-2">
            <Controller
              name={`${BASE}.${key}` as any}
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <div className="form-check">
                  <label className="form-check-label">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (!e.target.checked) {
                          resetField(`${BASE}.${key}_props` as any);
                        }
                      }}
                    />
                    {label}
                  </label>
                </div>
              )}
            />

            {checked && (
              <div className="row mt-2 ms-4">
                {key === "other" && (
                  <div className="col-lg-6 mb-2">
                    <div className="row">
                      <label className="form-label col-lg-4 col-md-4 col-12">
                        Treatment Name <span>*</span>
                      </label>
                      <div className="col-lg-8 col-md-8 col-12">
                        <Controller
                          name={`${BASE}.other_props.other_name` as any}
                          control={control}
                          defaultValue=""
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <input
                                type="text"
                                {...field}
                                className="form-control"
                                placeholder="Please specify the treatment name"
                              />
                              {error && (
                                <div className="text-danger text-small mt-1">{error.message}</div>
                              )}
                            </>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <div className="col-lg-6">
                  <div className="row">
                    <label className="form-label col-lg-4 col-md-4 col-12">
                      Failure Reason <span>*</span>
                    </label>
                    <div className="col-lg-8 col-md-8 col-12">
                      <Controller
                        name={`${BASE}.${key}_props.${key}_failure_reason` as any}
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Select
                              options={failureReasons.map((r) => ({ value: r, label: r }))}
                              value={field.value ? { value: field.value, label: field.value } : null}
                              onChange={(val) => field.onChange(val ? val.value : "")}
                              onBlur={field.onBlur}
                              placeholder="Select Reason"
                              classNames={{ control: () => `input-group ${error?.message ? "border-danger" : ""}` }}
                              noOptionsMessage={() => "No options"}
                            />
                            {error && (
                              <div className="text-danger text-small mb-1">{error.message}</div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-lg-6">
                  <div className="row">
                    <label className="form-label col-lg-4 col-md-4 col-12">
                      Date Tried <span>*</span>
                    </label>
                    <div className="col-lg-8 col-md-8 col-12">
                      <Controller
                        name={`${BASE}.${key}_props.${key}_tried_date` as any}
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <div className="position-relative">
                              <DatePicker
                                selected={field.value ? parse(field.value, "yyyy-MM-dd", new Date()) : null}
                                onBlur={field.onBlur}
                                onChange={(date) => {
                                  field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                                }}
                                dateFormat="MM/dd/yyyy"
                                className="form-control"
                                placeholderText="mm/dd/yyyy"
                                showMonthDropdown
                                showYearDropdown
                                maxDate={new Date()}
                                customInput={
                                  <ReactInputMask mask="99/99/9999">
                                    {(inputProps: {}) => <input {...inputProps} />}
                                  </ReactInputMask>
                                }
                                wrapperClassName="w-100"
                              />
                              <i
                                className="bi bi-calendar position-absolute"
                                style={{
                                  right: "12px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  pointerEvents: "none",
                                }}
                              />
                            </div>
                            {error && (
                              <div className="text-danger text-small mt-1">{error.message}</div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {selectedReason === "Other" && (
                  <div className="col-lg-12 mt-2">
                    <div className="row">
                      <label className="form-label col-lg-2 col-md-4 col-12">
                        Please specify <span>*</span>
                      </label>
                      <div className="col-lg-10 col-md-8 col-12">
                        <Controller
                          name={`${BASE}.${key}_props.${key}_failure_reason_other` as any}
                          control={control}
                          defaultValue=""
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <input
                                type="text"
                                {...field}
                                className="form-control"
                                placeholder="Please describe the failure reason"
                              />
                              {error && (
                                <div className="text-danger text-small">{error.message}</div>
                              )}
                            </>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default MedCheckboxList;